import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import DashboardHeader from '../home/components/DashboardHeader';
import { useAuth } from '../../contexts/useAuth';
import { useMainCourante } from '../../contexts/useMainCourante';
import { MainCouranteMessage } from '../../contexts/MainCouranteContext';
import { Send, Pencil, Trash2, X, Check, CheckCheck } from 'lucide-react';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function MainCourantePage() {
  const { user, authorizedApps } = useAuth();
  const {
    messages,
    loading,
    error,
    unreadCount,
    createMessage,
    updateMessage,
    deleteMessage,
    markAsRead,
  } = useMainCourante();

  const canAccess = authorizedApps.includes('hub_maincourante');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    markAsRead();
  }, [markAsRead]);

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    const ok = await createMessage(title, description);
    setSubmitting(false);
    if (ok) {
      setTitle('');
      setDescription('');
    }
  };

  const startEdit = (m: MainCouranteMessage) => {
    setEditingId(m.id);
    setEditTitle(m.title);
    setEditDescription(m.description);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: string) => {
    if (!editTitle.trim() || !editDescription.trim()) return;
    setSaving(true);
    const ok = await updateMessage(id, editTitle, editDescription);
    setSaving(false);
    if (ok) setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await deleteMessage(id);
  };

  return (
    <div className="min-h-screen bg-hub-bg bg-police-lights font-sans overflow-x-hidden" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <DashboardHeader />

      <main className="relative z-[1] pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold uppercase tracking-widest text-hub-text">
              Main Courante
            </h1>
            {unreadCount > 0 && (
              <span className="text-xs font-bold text-white bg-red-600 px-3 py-1 rounded-full whitespace-nowrap">
                {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
              </span>
            )}
            {messages.length > 0 && (
              <button
                onClick={markAsRead}
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-hub-text hover:text-hub-accent transition-colors"
              >
                <CheckCheck size={14} />
                Marquer tout comme lu
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-600/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Formulaire de publication */}
          <form
            onSubmit={handleSubmit}
            className="bg-hub-card border border-hub-border rounded-xl p-5 space-y-3"
          >
            <input
              type="text"
              placeholder="Titre"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text placeholder-hub-muted"
            />
            <textarea
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text placeholder-hub-muted resize-y"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !title.trim() || !description.trim()}
                className="flex items-center gap-2 bg-hub-blue hover:bg-hub-blue-hover text-white font-bold text-xs uppercase tracking-widest px-5 py-2.5 rounded disabled:opacity-50"
              >
                <Send size={14} />
                {submitting ? 'Publication...' : 'Publier'}
              </button>
            </div>
          </form>

          {/* Liste des messages */}
          <div className="space-y-4">
            {loading ? (
              <p className="text-hub-muted text-sm">Chargement...</p>
            ) : messages.length === 0 ? (
              <p className="text-hub-muted text-sm">Aucun message pour le moment.</p>
            ) : (
              messages.map((m) => (
                <article
                  key={m.id}
                  className="bg-hub-card border border-hub-border rounded-xl p-5"
                >
                  {editingId === m.id ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 rounded bg-hub-bg border border-hub-border text-hub-text resize-y"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => saveEdit(m.id)}
                          disabled={saving}
                          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-3 py-1.5 rounded disabled:opacity-50"
                        >
                          <Check size={14} />
                          {saving ? '...' : 'Enregistrer'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center gap-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded"
                        >
                          <X size={14} />
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h2 className="font-semibold text-hub-text">{m.title}</h2>
                          <p className="text-xs text-hub-muted mt-0.5">
                            {m.author_name} · {formatDate(m.created_at)} à {formatTime(m.created_at)}
                            {m.updated_at !== m.created_at && (
                              <span className="ml-2 italic">(modifié)</span>
                            )}
                          </p>
                        </div>

                        {user && m.author_id === user.id && (
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => startEdit(m)}
                              className="p-1.5 text-hub-muted hover:text-hub-accent transition-colors"
                              aria-label="Modifier"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(m.id)}
                              className="p-1.5 text-hub-muted hover:text-red-500 transition-colors"
                              aria-label="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="mt-3 text-sm text-hub-text whitespace-pre-wrap">
                        {m.description}
                      </p>
                    </>
                  )}
                </article>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
